import { store, store$ } from '@redux/store';
import fastdom from 'fastdom';
import { debounceTime, distinctUntilChanged, filter, map, merge, of, skip, switchMap, tap} from "rxjs";
import { selectPage, selectPageSize, onArticleCountResultsLoaded$ } from 'features/articleSearchResultsFacets';
import { routingSelectors , buildNewUrl, getPaging } from 'features/routing';

const paginationContainer = document.querySelector('[data-component="blog-article-search-results-pagination"]');
const previousButton = paginationContainer.querySelector('[data-prev-button]');
const nextButton = paginationContainer.querySelector('[data-next-button]');
const optionList = paginationContainer.querySelector('[data-option-list]');
const { urlComponentsSelector } = routingSelectors;
const getCountResults = () => Number(paginationContainer?.dataset?.countResults || 0);
const isB1Styles = () => !!document.querySelector('[data-b1-styles]');

/**
   * Get the URL reference by combining the URL components and the paging segment.
   * @param page - number of page to create the pagin segment of the url
   * @param urlComponents - urlComponents is an object that contains various components of a URL, such
   * as the protocol, domain, path, query parameters, etc.
*/
const getPageLinkUrl = function(page, urlComponents) {
    const pagingSegment = getPaging(page);
    const urlSegments = { ...urlComponents, urlPaging: pagingSegment };
    return buildNewUrl(urlSegments);
};

/**
   * Get a list of the pages to display in the pagination html
   * @param totalPages - number of pages to display in the pagination
   * @param currentPage - current number of page to display as active on pagination
*/
const getPageNumberList = function (totalPages, currentPage) {
    const pages = [];
    const maximumPagesToShow = 7;

    // When we can show all the pages without three dots.
    if (maximumPagesToShow >= totalPages) {
      // Gets array of number until the page count.
      return [...Array(totalPages).keys()].map((index) => index + 1);;
    }

    const firstPage = 1;
    const lastPage = totalPages;
    // We will need always the first page.
    pages.push(firstPage);

    const pageNumberToRight = lastPage - currentPage;
    const pageNumberToLeft = currentPage - firstPage;

    // This scenario is when the first page and the last have a distant of more than 3 pages with the active page.
    if (pageNumberToLeft > 3 && pageNumberToRight > 3) {
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
    }

    // This scenario is when the first page does not have a distant of 3 or more pages with the active page.
    if (pageNumberToLeft <= 3) {
      pages.push(firstPage + 1);
      pages.push(firstPage + 2);
      pages.push(firstPage + 3);
      pages.push(firstPage + 4);
    }

    // This scenario is when the last page does not have a distant of 3 or more pages with the active page.
    if (pageNumberToRight <= 3) {
      pages.push(lastPage - 4);
      pages.push(lastPage - 3);
      pages.push(lastPage - 2);
      pages.push(lastPage - 1);
    }

    // We will need always the last page.
    pages.push(lastPage);

    return pages;
};

/**
   * Get the html for pagination based on the next parameters
   * @param pageNumberList - number of pages to display in the pagination
   * @param currentPage - current number of page to display as active on pagination
   * @param urlComponents - urlComponents is an object that contains various components of a URL, such
   * as the protocol, domain, path, query parameters, etc.
*/
const getPaginationHtml = function(pageNumberList, currentPage, urlComponents) {
    return pageNumberList.reduce((accumulator, pageNumber, pageIndex) => {
        const previousPageNumber = pageNumberList[pageIndex - 1] || 0;
        const threeDotsElement = previousPageNumber + 1 !== pageNumber
            ? `<li class="paging__list-item">
                <span class="paging__spacer">...</span>
              </li>` : '';

        const currentPageClasses = pageNumber === currentPage ? "active" : '';

        const pageLink = 
            `<li class="paging__list-item">
                <a data-page-link="${pageNumber}" href="${getPageLinkUrl(pageNumber, urlComponents)}" aria-label="Go to ${pageNumber} page" class="paging__link ${currentPageClasses}">${pageNumber}</a>
            </li>`;

        return `${accumulator}${threeDotsElement}${pageLink}`;
    }, '');
}

/**
 * Generates the HTML for the pagination bar with B1 styles.
 * This will be removed after article search result rebrand
 * @param {Array<number>} pageNumberList - The list of page numbers.
 * @param {number} currentPage - The current page number.
 * @param {Object} urlComponents - The URL components.
 * @returns {string} The HTML string for the pagination bar.
 */
const getB1PaginationHtml = function(pageNumberList, currentPage, urlComponents) {
    return pageNumberList.reduce((accumulator, pageNumber, pageIndex) => {
        const previousPageNumber = pageNumberList[pageIndex - 1] || 0;
        const threeDotsElement = previousPageNumber + 1 !== pageNumber
            ? `<li class="nhs-c-pagination__spacer-container nhs-mq-pagination__spacer-container--extra-large"><span>...</span></li>` : '';

        const currentPageClasses = pageNumber === currentPage ? "nhs-c-pagination__number--active" : '';

        const pageLink =
            `<li class="nhs-c-pagination__number-container nhs-mq-pagination__number-container--extra-large" data-qa="pagination_number_container">
                <a data-page-link="${pageNumber}" href="${getPageLinkUrl(pageNumber, urlComponents)}/" aria-label="Go to ${pageNumber} page" class="nhs-c-pagination__number nhs-mq-pagination__number--extra-large ${currentPageClasses}">${pageNumber}</a>
            </li>`;

        return `${accumulator}${threeDotsElement}${pageLink}`;
    }, '');
};

// function to render the pagination after changes on the state
const updatePaginationBar = function({ paginationHtml, currentPage, totalPages }){
    fastdom.mutate(() => {
        previousButton.disabled = currentPage === 1;
        nextButton.disabled = currentPage === totalPages;
        optionList.innerHTML = paginationHtml
    });
};
  
// observer to map search command but only emmit if the page have been changed
const onPageChange$ = store$.pipe(
    map(selectPage),
    filter(Boolean),
    distinctUntilChanged(),
    map(getCountResults),
    skip(1)
);

// observer to map search command but only emmit if the page have been changed
const onPageSizeChange$ = store$.pipe(
    map(selectPageSize),
    filter(Boolean),
    distinctUntilChanged(),
    map(getCountResults),
    skip(1)
);

// observer to know if there are new search results
const newCountResults$ = onArticleCountResultsLoaded$.pipe(
    tap((countResults) => {paginationContainer.dataset.countResults = countResults})
);
  
// observer to render the pagination with the new settings based on
// new search results 
// new page number change
// new page size change
const onPaginationChange$ = merge(onPageChange$, onPageSizeChange$, newCountResults$).pipe(
    filter((countResults) => countResults > 20),
    debounceTime(500),
    switchMap((countResults) => {
        const state = store.getState();
        const currentPage = selectPage(state);
        const currentPageSize = selectPageSize(state);
        const urlComponents = urlComponentsSelector(state);
        const totalPages = Math.ceil(countResults / currentPageSize);
        const pageNumberList = getPageNumberList(totalPages, currentPage);
        const paginationHtml = isB1Styles()
            ? getB1PaginationHtml(pageNumberList, currentPage, urlComponents)
            : getPaginationHtml(pageNumberList, currentPage, urlComponents);
        return of({ paginationHtml, currentPage, totalPages });
    }),
    distinctUntilChanged()
);
onPaginationChange$.subscribe(updatePaginationBar);